import cx from "classnames";
import { useIsomorphicLayoutEffect } from "framer-motion";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useRafState } from "react-use";

export type TextboxAutoProps = React.DetailedHTMLProps<
	React.TextareaHTMLAttributes<HTMLTextAreaElement>,
	HTMLTextAreaElement
> & {
	expandable?: boolean;
};

export const TextboxAuto = React.forwardRef<HTMLTextAreaElement, TextboxAutoProps>(
	function TexboxAutoComponent({ className, style, expandable = true, ...props }, incomingRef) {
		const container = useRef<HTMLDivElement | null>(null);
		const textbox = useRef<HTMLTextAreaElement | null>(null);
		const bufferbox = useRef<HTMLTextAreaElement | null>(null);
		const [height, setHeight] = useRafState("0px");

		const updateHeight = useCallback(() => {
			if (!expandable) return;

			const box = textbox.current;
			const buf = bufferbox.current;

			if (box && buf) {
				buf.style.height = "0px";

				// For uncontrolled components (using defaultValue), we need to get the current
				// value from the textbox itself rather than props.value which may be undefined
				const currentValue = box.value || "";
				buf.value = currentValue;

				if (currentValue.slice(-1) === "\n") {
					buf.value += " ";
				}

				const heightString = `${Math.ceil(buf.scrollHeight)}px`;

				box.style.height = heightString;

				if (heightString !== height) {
					setHeight(heightString);
				}
			}
		}, [height, props.value, expandable]);

		useEffect(() => {
			if (!expandable) return;

			const containerEl = container.current;

			if (!containerEl) {
				return undefined;
			}

			const resizeObserver = new ResizeObserver(() => {
				updateHeight();
			});

			// watch for resize events
			resizeObserver.observe(containerEl);

			// cleanup
			return () => {
				resizeObserver.disconnect();
			};
		}, [updateHeight, expandable]);

		useIsomorphicLayoutEffect(() => {
			updateHeight();
		}, [props.value]);

		// IMPORTANT: pb-1 to avoid cutting off the bottoms of lowercase y, j, g, p, q, and other
		// letters that have a stem.
		const classNames = cx(className, "pb-1", {
			truncate: !expandable,
		});
		const styles = useMemo(() => {
			const baseStyles: Record<string, string> = {
				boxSizing: "border-box",
				resize: "none",
				overflow: "hidden",
				width: "100%",
			};

			// Only apply dynamic height when expandable
			if (expandable) {
				baseStyles.height = height;
			}

			return Object.assign(style || {}, baseStyles);
		}, [style, height, expandable]);

		const sharedRefs = (el: HTMLTextAreaElement) => {
			textbox.current = el;

			if (!incomingRef) {
				return;
			}

			if (typeof incomingRef === "function") {
				incomingRef(el);
			} else {
				incomingRef.current = el;
			}
		};

		const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			// Call the original onChange if provided
			if (props.onChange) {
				props.onChange(e);
			}

			// Trigger height update for uncontrolled components only if expandable
			if (expandable) {
				updateHeight();
			}
		};

		return (
			<div className={cx("w-full", "print:hidden", "relative")} ref={container}>
				{expandable ? (
					<>
						{/* This textarea is visible and expandable */}
						<textarea
							ref={sharedRefs}
							rows={1}
							className={classNames}
							style={styles}
							{...props}
							onChange={handleChange}
						/>
						{/* We use the scroll height of this invisible box to detect height changes */}
						<textarea
							aria-hidden
							ref={bufferbox}
							rows={1}
							className={classNames}
							style={{
								...styles, // Copy all the same styles as the main textarea
								width: "100%",
								position: "absolute",
								top: "0",
								left: "0",
								height: "0px",
								display: "block",
								visibility: "hidden",
								pointerEvents: "none",
								resize: "none",
								overflow: "hidden",
							}}
							value={props.value || "&nbsp;"}
							disabled
						/>
					</>
				) : (
					/* This input is for non-expandable fields with ellipsis */
					<input
						ref={sharedRefs as any}
						type="text"
						className={classNames}
						style={styles}
						{...(props as any)}
						onChange={handleChange as any}
					/>
				)}
			</div>
		);
	}
);
